import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '@/hooks/useTenant';
import {
  fetchMerchantEvents, eventImage, eventShortDate, eventTimeLabel, isEventPast,
  type PublicEvent,
} from '@/lib/publicEvents';

type Filter = 'upcoming' | 'past' | 'all';

export default function EventsPage() {
  const { tenant, isTenant, loading: tenantLoading } = useTenant();
  const [events, setEvents] = useState<PublicEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('upcoming');
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (tenantLoading) return;
    if (!isTenant || !tenant) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const rows = await fetchMerchantEvents(tenant.id);
        if (!cancelled) setEvents(rows);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Impossible de charger les événements.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isTenant, tenant, tenantLoading]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return events.filter((ev) => {
      const past = isEventPast(ev);
      if (filter === 'upcoming' && past) return false;
      if (filter === 'past' && !past) return false;
      if (!q) return true;
      return `${ev.title || ''} ${ev.description || ''}`.toLowerCase().includes(q);
    });
  }, [events, filter, query]);

  const upcomingCount = events.filter((e) => !isEventPast(e)).length;

  if (!isTenant || !tenant) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
          <i className="ri-calendar-event-line text-2xl text-foreground-400"></i>
        </div>
        <h1 className="text-xl font-bold font-heading text-foreground-950 mb-2">Événements</h1>
        <p className="text-sm text-foreground-500 max-w-md">
          Retrouvez l&apos;agenda des événements directement sur le site du marchand.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background-100 via-background-50 to-secondary-50"></div>
        <div className="relative w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div className="max-w-2xl">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold mb-4">
                  <i className="ri-calendar-event-line"></i>
                  Agenda de {tenant.nomcommerce || tenant.name}
                </span>
                <h1 className="text-3xl md:text-5xl font-bold font-heading text-foreground-950 leading-tight mb-4">
                  Nos <span className="text-accent-600">événements</span>
                </h1>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  Concerts, ateliers, portes ouvertes, soirées : découvrez les prochains rendez-vous
                  et ouvrez la fiche de chaque événement pour tous les détails.
                </p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="px-4 py-3 rounded-lg bg-background-50 border border-background-200/70">
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-foreground-400">À venir</p>
                  <p className="text-xl font-bold font-heading text-foreground-950">{upcomingCount}</p>
                </div>
                <div className="px-4 py-3 rounded-lg bg-background-50 border border-background-200/70">
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-foreground-400">Total publié</p>
                  <p className="text-xl font-bold font-heading text-foreground-950">{events.length}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filtres */}
      <section className="w-full px-4 md:px-6 max-w-7xl mx-auto -mt-6 relative z-10">
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-3 md:p-4 flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex items-center gap-1 p-1 bg-background-100 rounded-full w-fit">
            {([
              { key: 'upcoming' as Filter, label: 'À venir' },
              { key: 'past' as Filter, label: 'Passés' },
              { key: 'all' as Filter, label: 'Tous' },
            ]).map((tab) => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                  filter === tab.key ? 'bg-background-50 text-foreground-950' : 'text-foreground-500 hover:text-foreground-800'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher un événement…"
              className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
            />
          </div>
          <span className="text-xs text-foreground-500 whitespace-nowrap">
            {filtered.length} événement{filtered.length > 1 ? 's' : ''}
          </span>
        </div>
      </section>

      {/* Liste */}
      <section className="w-full px-4 md:px-6 max-w-7xl mx-auto py-10 md:py-14">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <i className="ri-error-warning-line text-4xl text-red-400 mb-3"></i>
            <p className="text-sm text-foreground-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              Réessayer
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-calendar-line text-2xl text-foreground-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-foreground-800 mb-1">Aucun événement</h3>
            <p className="text-sm text-foreground-500 max-w-md">
              Aucun événement ne correspond à ce filtre pour le moment. Revenez bientôt.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">
            {filtered.map((ev) => {
              const cover = eventImage(ev);
              const date = eventShortDate(ev.start_datetime);
              const time = eventTimeLabel(ev.start_datetime);
              const past = isEventPast(ev);
              return (
                <Link
                  key={ev.id}
                  to={`/events/${ev.id}`}
                  className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-background-300/60 transition-colors no-underline flex flex-col"
                >
                  <div className="relative h-44 bg-background-100 overflow-hidden">
                    {cover ? (
                      <img
                        src={cover}
                        alt={ev.title || 'Événement'}
                        title={`${ev.title || 'Événement'} — agenda`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-secondary-100 to-accent-50">
                        <i className="ri-calendar-event-line text-5xl text-secondary-300"></i>
                      </div>
                    )}
                    <div className="absolute top-3 left-3 bg-background-50/95 backdrop-blur-sm rounded-md px-2.5 py-1.5 text-center min-w-[52px]">
                      <p className="text-base font-bold font-heading text-foreground-950 leading-none">{date.day}</p>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-accent-700 mt-0.5">{date.month}</p>
                    </div>
                    {past && (
                      <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-foreground-950/70 text-background-50 text-[10px] font-semibold">
                        Terminé
                      </span>
                    )}
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {ev.title || 'Événement'}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-2 flex items-center gap-1.5">
                      <i className="ri-calendar-line"></i>
                      <span className="truncate">{date.full}{time ? ` · ${time}` : ''}</span>
                    </p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      {Number(ev.price || 0) > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 text-[10px] font-bold">
                          <i className="ri-price-tag-3-line"></i>
                          {Number(ev.price).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {ev.currency || 'EUR'}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 text-[10px] font-semibold">
                          <i className="ri-gift-line"></i>Gratuit
                        </span>
                      )}
                      {ev.location && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[10px] font-medium truncate max-w-full">
                          <i className="ri-map-pin-line"></i>{ev.location}
                        </span>
                      )}
                    </div>
                    {ev.description && (
                      <p className="text-xs text-foreground-600 leading-relaxed line-clamp-3 mt-2">{ev.description}</p>
                    )}
                    <span className="mt-auto pt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600">
                      Voir l&apos;événement
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}